import { Button, TextInput } from 'components/core';
import { ITeroOrderAdditionalInfoProps, Styled } from './';
import { FC, useState } from 'react';
import { TermsConditionsAcceptedModal } from './TermsConditionsAcceptedModal';
import { useRouter } from 'next/router';
import { useForm } from 'utils/hooks';
import { useI18n } from 'contexts/i18n';
import {
  isDeliveryFieldVisible,
  isLicenseFieldAvailable,
} from 'components/ITeroCheckout';
import DateFieldWithPicker from './DateFieldWithPicker';

const ITeroOrderAdditionalInfo: FC<ITeroOrderAdditionalInfoProps> = ({
  mode,
  onSave,
  additionalOrderInfo,
  setAdditionalOrderInfo,
  existingScannerSerialNumber,
}) => {
  const [showTCAcceptedModal, setShowTCAcceptedModal] = useState(false);
  const [directPhone, setDirectPhone] = useState(
    additionalOrderInfo.directPhone
  );
  const [customerPhone, setCustomerPhone] = useState(
    additionalOrderInfo.doctorPhone
  );
  const [contactEmail, setContactEmail] = useState(
    additionalOrderInfo.contactEmail
  );
  const [scannerEmail, setScannerEmail] = useState(
    additionalOrderInfo.scannerLoginEmail
  );
  const [directEmail, setDirectEmail] = useState(
    additionalOrderInfo.directEmail
  );
  const [deliveryDate, setDeliveryDate] = useState(
    additionalOrderInfo.deliveryDate || ''
  );
  const i18n = useI18n();
  const { handleValidChange, allValid } = useForm([
    'scannerLoginEmail',
    'directEmail',
    'doctorPhone',
    'directPhone',
  ]);

  const showLicenceFields = isLicenseFieldAvailable(
    additionalOrderInfo.licenceNumber,
    additionalOrderInfo.stateOfIssuance
  );

  const showDeliveryFeild = isDeliveryFieldVisible(
    additionalOrderInfo.deliveryDate
  );

  const router = useRouter();

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    const updateState = (
      setState: React.Dispatch<React.SetStateAction<string>>,
      newValue: string
    ) => {
      setState(newValue);
      setAdditionalOrderInfo(prevState => ({
        ...prevState,
        [name]: newValue,
      }));
    };

    switch (name) {
      case 'directPhone':
        updateState(setDirectPhone, value);
        break;
      case 'doctorPhone':
        updateState(setCustomerPhone, value);
        break;
      case 'contactEmail':
        updateState(setContactEmail, value);
        break;
      case 'scannerLoginEmail':
        updateState(setScannerEmail, value);
        break;
      case 'directEmail':
        updateState(setDirectEmail, value);
        break;
      case 'deliveryDate':
        updateState(setDeliveryDate, value);
        break;
      default:
        setAdditionalOrderInfo(prevState => ({
          ...prevState,
          [name]: value,
        }));
        break;
    }
  };

  const isHavingEmptyInput = () => {
    const optionalFields = ['deliveryDate'];
    const hasEmptyValue = Object.entries(additionalOrderInfo)
      .filter(([key]) => !optionalFields.includes(key))
      .some(
        ([, value]) =>
          value === null || value === undefined || value.trim().length === 0
      );

    return hasEmptyValue;
  };

  const isButtonDisabled = !allValid || isHavingEmptyInput();

  return (
    <>
      {mode === 'create' && (
        <>
          <Styled.Container>
            <Styled.CustomerInfo>
              <Styled.Heading>{i18n['Customer Information']}</Styled.Heading>

              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Doctor Name']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    autoFocus="autoFocus"
                    name="doctorName"
                    value={additionalOrderInfo.doctorName}
                    onChange={handleChange}
                    width="100%"
                    disabled={true}
                    maxLength={30}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>

              {showLicenceFields && (
                <>
                  <Styled.TextInputWrapper>
                    <Styled.TextInputLabel>
                      {i18n['License Number']} <Styled.Required />
                    </Styled.TextInputLabel>
                    <Styled.InputContainer>
                      <TextInput
                        name="licenceNumber"
                        value={additionalOrderInfo.licenceNumber as string}
                        onChange={handleChange}
                        width="100%"
                        disabled={mode !== 'create'}
                        maxLength={30}
                      />
                    </Styled.InputContainer>
                  </Styled.TextInputWrapper>
                  <Styled.TextInputWrapper>
                    <Styled.TextInputLabel>
                      {i18n['Territory/State of Issuance']} <Styled.Required />
                    </Styled.TextInputLabel>
                    <Styled.InputContainer>
                      <TextInput
                        name="stateOfIssuance"
                        value={additionalOrderInfo.stateOfIssuance as string}
                        onChange={handleChange}
                        width="100%"
                        disabled={mode !== 'create'}
                        maxLength={30}
                      />
                    </Styled.InputContainer>
                  </Styled.TextInputWrapper>
                </>
              )}

              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Contact Email']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="contactEmail"
                    value={additionalOrderInfo.contactEmail}
                    onChange={handleChange}
                    width="100%"
                    disabled={true}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Scanner Registration Email']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="scannerLoginEmail"
                    value={scannerEmail}
                    onChange={handleChange}
                    width="100%"
                    disabled={mode !== 'create'}
                    pattern="^[^@]+@[^@]+\.[^@]+$"
                    onValidChange={handleValidChange('scannerLoginEmail')}
                    errMessage={i18n['Please enter a valid email address']}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
              {existingScannerSerialNumber &&
                existingScannerSerialNumber.length > 0 && (
                  <Styled.TextInputWrapper>
                    <Styled.TextInputLabel>
                      {i18n['Trade in/Upgrade Scanner(SN)']} <Styled.Required />
                    </Styled.TextInputLabel>
                    <Styled.InputContainer>
                      <TextInput
                        name="scannerLoginEmail"
                        value={existingScannerSerialNumber}
                        onChange={handleChange}
                        width="100%"
                        disabled={true}
                      />
                    </Styled.InputContainer>
                  </Styled.TextInputWrapper>
                )}

              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Phone Number']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="doctorPhone"
                    value={customerPhone}
                    onChange={handleChange}
                    width="100%"
                    pattern="^\+?[0-9\s\-()]{6,}$"
                    onValidChange={handleValidChange('doctorPhone')}
                    errMessage={i18n['Please enter a valid phone number']}
                    maxLength={20}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
            </Styled.CustomerInfo>

            <Styled.PrimaryOfficeContactInfo>
              <Styled.Heading>{i18n['Primary Office Contact']}</Styled.Heading>
              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['First Name']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="firstName"
                    value={additionalOrderInfo.firstName}
                    onChange={handleChange}
                    width="100%"
                    disabled={mode !== 'create'}
                    maxLength={30}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Last Name']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="lastName"
                    value={additionalOrderInfo.lastName}
                    onChange={handleChange}
                    width="100%"
                    disabled={mode !== 'create'}
                    maxLength={30}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Direct Phone']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="directPhone"
                    value={directPhone}
                    onChange={handleChange}
                    width="100%"
                    disabled={mode !== 'create'}
                    pattern="^\+?[0-9\s\-()]{6,}$"
                    onValidChange={handleValidChange('directPhone')}
                    errMessage={i18n['Please enter a valid phone number']}
                    maxLength={20}
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
              <Styled.TextInputWrapper>
                <Styled.TextInputLabel>
                  {i18n['Direct Email']} <Styled.Required />
                </Styled.TextInputLabel>
                <Styled.InputContainer>
                  <TextInput
                    name="directEmail"
                    value={directEmail}
                    onChange={handleChange}
                    width="100%"
                    disabled={mode !== 'create'}
                    pattern="^[^@]+@[^@]+\.[^@]+$"
                    onValidChange={handleValidChange('directEmail')}
                    errMessage="Please enter a valid email address"
                  />
                </Styled.InputContainer>
              </Styled.TextInputWrapper>
            </Styled.PrimaryOfficeContactInfo>
          </Styled.Container>
          {showDeliveryFeild && (
            <DateFieldWithPicker
              deliveryDate={deliveryDate}
              handleChange={handleChange}
            />
          )}
          {mode === 'create' && (
            <Styled.ButtonWrapper>
              <Button
                size="md"
                disabled={isButtonDisabled}
                onClick={() => {
                  onSave();
                }}
              >
                {i18n['Next']}
              </Button>
            </Styled.ButtonWrapper>
          )}
        </>
      )}
      {mode === 'edit' && (
        <Styled.OrderAdditionalInfoEditContainer>
          <Styled.AdditionalInfoContainer>
            <Styled.Title>{i18n['Customer Information']}</Styled.Title>
            <Styled.AdditionalInfoData>
              {i18n['Doctor Name']}: {additionalOrderInfo.doctorName} <br />
              {showLicenceFields && (
                <>
                  {`${i18n['Licence Number']}: ${additionalOrderInfo.licenceNumber}`}
                  <br />
                  {`${i18n['Territory/State of Issuance']}: ${additionalOrderInfo.stateOfIssuance}`}
                  <br />
                </>
              )}
              {i18n['Contact Email']}: {additionalOrderInfo.contactEmail} <br />
              {i18n['Scanner Registration Email']}:{' '}
              {additionalOrderInfo.scannerLoginEmail}
              {existingScannerSerialNumber &&
                existingScannerSerialNumber.length > 0 && (
                  <>
                    <br />
                    {i18n['Trade in/Upgrade Scanner(SN)']}:{' '}
                    {existingScannerSerialNumber}
                  </>
                )}
              <br />
              {i18n['Phone Number']}: {additionalOrderInfo.doctorPhone} <br />
              {additionalOrderInfo?.deliveryDate && (
                <>
                  {i18n['Earliest Delivery Date']}:{' '}
                  {additionalOrderInfo.deliveryDate}
                  <br />
                </>
              )}
              <br />
            </Styled.AdditionalInfoData>
          </Styled.AdditionalInfoContainer>
          <Styled.AdditionalInfoContainer>
            <Styled.Title>{i18n['Primary Office Contact']}</Styled.Title>
            <Styled.AdditionalInfoData>
              {i18n['First Name']}: {additionalOrderInfo.firstName} <br />
              {i18n['Last Name']}: {additionalOrderInfo.lastName} <br />
              {i18n['Direct Phone']}: {additionalOrderInfo.directPhone} <br />
              {i18n['Direct Email']}: {additionalOrderInfo.directEmail}
              <br />
            </Styled.AdditionalInfoData>
          </Styled.AdditionalInfoContainer>
        </Styled.OrderAdditionalInfoEditContainer>
      )}
      {showTCAcceptedModal && (
        <TermsConditionsAcceptedModal
          onClose={() => {
            setShowTCAcceptedModal(false);
            router.push('/');
          }}
        />
      )}
    </>
  );
};

export default ITeroOrderAdditionalInfo;
